"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { t } from "@/i18n/i18n";
import { Check, Loader2 } from "lucide-react";
import Image from "next/image";
import { useState } from "react";

export default function ForgotPasswordPage({
  params,
}: {
  params: { lang: string };
}) {
  const lang = params.lang ?? "en";
  const [email, setEmail] = useState("");
  const [requestId, setRequestId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [message, setMessage] = useState("");
  const [done, setDone] = useState(false);
  const [loadingStep, setLoadingStep] = useState<1 | 2 | 3 | null>(null);

  async function requestOtp() {
    setLoadingStep(1);
    const resp = await fetch("/api/auth/forgot-password/request", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, lang }),
    });
    const data = await resp.json();
    setMessage(data.message);
    if (data.requestId) setRequestId(data.requestId);
    setLoadingStep(null);
  }

  async function verifyOtp() {
    setLoadingStep(2);
    const resp = await fetch("/api/auth/forgot-password/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ requestId, otp, lang }),
    });
    const data = await resp.json();
    if (data.ok) {
      setOtpVerified(true);
      setMessage("");
    } else {
      setMessage(data.message);
    }
    setLoadingStep(null);
  }

  async function submitNewPassword() {
    setLoadingStep(3);
    const resp = await fetch("/api/auth/forgot-password/submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ requestId, newPw, lang }),
    });
    const data = await resp.json();
    setMessage(data.message ?? (data.ok ? "OK" : "Error"));
    if (data.ok) setDone(true);
    setLoadingStep(null);
  }

  const step1Done = !!requestId;
  const step2Done = otpVerified;
  const step3Done = done;

  const steps = [
    {
      label: t("forgotpw", "emailLabel", lang),
      done: step1Done,
      active: true,
      content: (
        <div className="space-y-2">
          <Input
            placeholder={t("forgotpw", "emailLabel", lang)}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={step1Done || loadingStep === 1}
          />
          <Button
            className="text-sm"
            onClick={requestOtp}
            disabled={step1Done || !email || loadingStep !== null}
          >
            {loadingStep === 1 && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("forgotpw", "emailButton", lang)}
          </Button>
        </div>
      ),
    },
    {
      label: t("forgotpw", "otpLabel", lang),
      done: step2Done,
      active: step1Done,
      content: (
        <div className="space-y-2">
          <Input
            placeholder={t("forgotpw", "otpLabel", lang)}
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            disabled={!step1Done || step2Done || loadingStep === 2}
          />
          <Button
            className="text-sm"
            onClick={verifyOtp}
            disabled={!step1Done || step2Done || !otp || loadingStep !== null}
          >
            {loadingStep === 2 && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("forgotpw", "otpButton", lang)}
          </Button>
        </div>
      ),
    },
    {
      label: t("forgotpw", "newPwLabel", lang),
      done: step3Done,
      active: step2Done,
      content: (
        <div className="space-y-2">
          <Input
            type="password"
            placeholder={t("forgotpw", "newPwLabel", lang)}
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            disabled={!step2Done || step3Done || loadingStep === 3}
          />
          <Button
            className="text-sm"
            onClick={submitNewPassword}
            disabled={!step2Done || step3Done || !newPw || loadingStep !== null}
          >
            {loadingStep === 3 && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("forgotpw", "saveButton", lang)}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex justify-center items-center bg-background">
      <Card className="w-full max-w-md p-6 space-y-6">
        <div className="flex flex-row justify-between">
          <Image
            src="https://images.gelitabrasil.tech/gelita"
            alt={t("login", "logoAlt", lang)}
            width={165}
            height={90}
          />
          <Image
            src="https://images.gelitabrasil.tech/bipp"
            alt={t("login", "logoAlt", lang)}
            width={185}
            height={85}
          />
        </div>

        <div>
          <h1 className="text-2xl font-bold">{t("forgotpw", "title", lang)}</h1>
          <p className="text-muted-foreground text-sm mt-1 whitespace-pre-line">
            {t("forgotpw", "description", lang)}
          </p>
        </div>

        <ol className="relative">
          {steps.map((step, i) => {
            const isLast = i === steps.length - 1;
            return (
              <li key={i} className="flex gap-4">
                {/* Timeline column */}
                <div className="flex flex-col items-center">
                  <div
                    className={[
                      "flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-semibold shrink-0 transition-colors",
                      step.done
                        ? "bg-primary border-primary text-primary-foreground"
                        : step.active
                          ? "border-primary text-primary"
                          : "border-muted-foreground/40 text-muted-foreground/40",
                    ].join(" ")}
                  >
                    {loadingStep === i + 1 ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : step.done ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      i + 1
                    )}
                  </div>
                  {!isLast && (
                    <div
                      className={[
                        "w-0.5 flex-1 my-1 min-h-4 transition-colors",
                        step.done ? "bg-primary" : "bg-muted-foreground/20",
                      ].join(" ")}
                    />
                  )}
                </div>

                {/* Step content */}
                <div className={["pb-6 flex-1", isLast ? "" : ""].join(" ")}>
                  <p
                    className={[
                      "text-sm font-medium mb-2",
                      step.active
                        ? "text-foreground"
                        : "text-muted-foreground/50",
                    ].join(" ")}
                  >
                    {step.label}
                  </p>
                  <div
                    className={
                      step.active ? "" : "pointer-events-none opacity-40"
                    }
                  >
                    {step.content}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>

        {message && (
          <p className="text-sm text-muted-foreground border-t pt-4">
            {message}
          </p>
        )}
      </Card>
    </div>
  );
}
